import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useOvertime } from "@/contexts/overtime/useOvertime";
import RequestTable from "@/components/dashboard/RequestTable";
import { InfoIcon } from "lucide-react";
import { useAuth } from "@/contexts/auth";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
;
import { toast } from "sonner";
import { hasStatusOf } from "@/contexts/overtime/utils/filterUtils";
import { useAllEmployees } from "@/hooks/useQueryHooks";
import { OvertimeStatus, UserRole } from "@/types/enums";

const PendingRequests = () => {
  const { overtimeRequests, refreshRequests } = useOvertime();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch all employees for mapping employee_id
  const { data: employees = [], refetch: refetchEmployees } = useAllEmployees({ enabled: true });
  
  // Create a comprehensive refresh function that reloads all data
  const refreshAllData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      // Refresh overtime requests
      await refreshRequests();
      
      // Refresh employee data
      await refetchEmployees();
      
    } catch {
      toast.error('Failed to refresh data');
    } finally {
      setIsLoading(false);
    }
  }, [user, refreshRequests, refetchEmployees]);
  const employeeIdMap = useMemo(() => {
    const map = new Map();
    employees.forEach(emp => {
      if (emp.id && emp.employee_id) map.set(emp.id, emp.employee_id);
    });
    return map;
  }, [employees]);

  // Use a simple effect to load data once when the component mounts
  // or when the user changes
  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        await refreshRequests();
      } catch {
        toast.error('Failed to load pending requests');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [user, refreshRequests]);

  const filteredRequests = useMemo(() => {
    const allRequests = overtimeRequests || [];
    let filtered = allRequests;

    if (user?.role === UserRole.PLANT_MANAGER || user?.role === UserRole.HR) {
      // Plant Manager: Must filter by department if they have assigned departments
      // HR: Can see all requests across all departments
      filtered = filtered.filter(req => {
        // For HR, no department constraint - they can see all requests
        if (user?.role === UserRole.HR) {
          return (
            // HR now only sees Superintendent requests with 'pending_dh_approval' status
            (req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT && 
             hasStatusOf(req.status, [OvertimeStatus.PENDING_DH_APPROVAL]))
          );
        }
        
        // For Plant Manager, enforce department constraint
        if (user?.role === UserRole.PLANT_MANAGER) {
          // If Plant Manager has no departments assigned, they see nothing
          if (!user.department || user.department.length === 0) return false;
          
          // Plant Manager can only see requests from their assigned departments
          const inTheirDepartment = user.department.includes(req.department);
          return inTheirDepartment && (
            // 1. Department Head requests: status 'pending' and not requiring VP approval
            (req.created_by_role?.toLowerCase() === UserRole.DEPARTMENT_HEAD && 
             hasStatusOf(req.status, [OvertimeStatus.PENDING]) &&
             !req.vp_approval_required) ||
            // 2. Superintendent requests: status 'pending_pm_approval' (approved by DH, needs PM approval)
            (req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT && 
             hasStatusOf(req.status, [OvertimeStatus.PENDING_PM_APPROVAL]))
          );
        }
        
        return false;
      });
    } else if (user?.role === UserRole.SUPER_ADMIN) {
      filtered = filtered.filter(req => 
        // 1. status - pending or pending_vp_approval AND role - plant_manager
        (hasStatusOf(req.status, [OvertimeStatus.PENDING, OvertimeStatus.PENDING_VP_APPROVAL]) && 
         req.created_by_role?.toLowerCase() === UserRole.PLANT_MANAGER) ||
        // 2. status - pending or pending_vp_approval AND role - department_head AND vp_approval_required = true
        (hasStatusOf(req.status, [OvertimeStatus.PENDING, OvertimeStatus.PENDING_VP_APPROVAL]) && 
         req.created_by_role?.toLowerCase() === UserRole.DEPARTMENT_HEAD && 
         req.vp_approval_required === true) ||
        // 3. Over-threshold superintendent requests that go directly to VP
        (hasStatusOf(req.status, [OvertimeStatus.PENDING_VP_APPROVAL]) && 
         req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT)
      );
    } else if (user?.role === UserRole.DEPARTMENT_HEAD) {
      // Department Head should ONLY see superintendent requests with 'pending_dh_approval' status
      filtered = filtered.filter(req => 
        user.department && 
        user.department.includes(req.department) && 
        req.created_by_role?.toLowerCase() === UserRole.SUPERINTENDENT && 
        hasStatusOf(req.status, [OvertimeStatus.PENDING_DH_APPROVAL])
      );
    }

    return filtered;
  }, [overtimeRequests, user?.role, user?.department]);

  // Map employee_id from employees table to each request
  const requestsWithDisplayId = useMemo(() => {
    return filteredRequests.map(req => ({
      ...req,
      employee_display_id: employeeIdMap.get(req.employee_id) || 'Unknown'
    }));
  }, [filteredRequests, employeeIdMap]);

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between">
        <div>
          {/* Remove the <h1 className="text-2xl font-bold tracking-tight">Pending Approvals</h1> heading */}
        </div>
      </div>
      
      {(user?.role === UserRole.PLANT_MANAGER || user?.role === UserRole.HR || user?.role === UserRole.DEPARTMENT_HEAD) && (
        <Alert variant="default" className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
          <InfoIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertTitle className="text-foreground">Overtime Approval Thresholds</AlertTitle>
          <AlertDescription className="text-sm mt-1 text-foreground">
            Requests that exceed the company's monthly overtime threshold will be automatically routed to 
            VP/Super Admin for approval and won't appear in your pending list. VP approval is required when an 
            employee's total monthly overtime hours exceed the company limit.
          </AlertDescription>
        </Alert>
      )}
      

      
      {(user?.role === UserRole.PLANT_MANAGER || user?.role === UserRole.HR || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.DEPARTMENT_HEAD) ? (
        <RequestTable 
          requests={requestsWithDisplayId} 
          showActions={true} 
          isLoading={isLoading}
          config={{
            showRefresh: true,
            showSearch: true,
            showExport: true,
            showColumnVisibility: true
          }}
          onRefresh={refreshAllData}
        />
      ) : (
        <Alert variant="destructive" className="bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800">
          <AlertTitle className="text-destructive">Access Denied</AlertTitle>
          <AlertDescription className="text-sm mt-1 text-destructive">
            You do not have permission to view this page.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PendingRequests;
